import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import './DeskNotes.css';
import { claimChime, sweepChimeClaims } from './lib/chimeClaim';
import { isMulti, buildSections, nextFilter } from './lib/deskSections';
import { LOCATIONS } from './context/LocationContext'; // plain data: label/short/colour per center

// DeskNotes — the front-desk sticky-note panel that the desk-notes Chrome
// extension (extensions/desk-notes/content.js) iframes onto Zenoti pages.
// Served at /desk-notes OUTSIDE the authed app shell (see src/index.js): it
// self-authorizes with a desk device token (localStorage['desk-token']) and
// talks to exactly one backend surface — /api/desk/change-requests. It never
// touches Zenoti; the 15 s poll is DB-only by backend contract.
//
// Visual contract: Lindsay's approved mockup (guest-price-ok-gate artifact).
// Spec: docs/superpowers/specs/2026-09-25-appointment-change-requests-design.md §Desk surface
// Plan: docs/superpowers/plans/2026-09-25-appointment-change-requests.md Task 12
//
// v1 note: rows carry no appointment times, so there is no IN SERVICE tag —
// open cards are just newest-first with kioskWaitingAt rows pinned on top
// (GUEST AT KIOSK). Deriving in-service client-side isn't possible from this
// payload; add it only if the API ever exposes the appointment window.

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const POLL_MS = 15000;
const TOKEN_KEY = 'desk-token';
const MUTED_KEY = 'desk-notes-muted';
const MINIMIZED_KEY = 'desk-notes-minimized';
// Placement handshake with extensions/desk-notes/content.js (v1.2+): the
// extension is a dumb shell — this page decides where the iframe sits and how
// big it is, so placement changes ship through ops, never the extension.
//   panel → parent: { type:'cp-desk-notes:hello' }  (ask for the viewport)
//   parent → panel: { type:'cp-desk-notes:viewport', width, height }
//   panel → parent: { type:'cp-desk-notes:rect', left, top, width, height }
// Minimized, the iframe shrinks to the pill (+ PILL_INSET for its shadow): a
// full-size transparent frame swallowed clicks on Zenoti's Book/Save.
const ZENOTI_ORIGIN = 'https://cutiepiewaxbar.zenoti.com';
const EXT_ORIGIN_OK = (o) => o === ZENOTI_ORIGIN;
const PILL_INSET = 12;
const EDGE = 18; // default gap from the screen edge
// Anchor = the panel's bottom-left corner, as px from the viewport's left and
// bottom. The open panel and the pill share it, so minimizing leaves the pill
// where the panel's corner was. Dragged via the header or the pill.
const POS_KEY = 'desk-notes-pos';
const DEFAULT_POS = { left: EDGE, bottom: EDGE };
// Open-panel size, drag-resizable from its top-right grip.
const SIZE_KEY = 'desk-notes-size';
const DEFAULT_SIZE = { width: 348, height: 520 };
const MIN_SIZE = { width: 260, height: 240 };
const MAX_WIDTH = 600;
function readJson(key, fallback, ...fields) {
  try {
    const v = JSON.parse(localStorage.getItem(key));
    if (fields.every((f) => Number.isFinite(v?.[f]))) return v;
  } catch { /* bad JSON — fall through */ }
  return fallback;
}
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));

// ── Pacific-as-UTC clock ────────────────────────────────────────────────────
// All ChangeRequest datetimes are Pacific wall time stored in the UTC fields
// (repo-wide convention — see backend/lib/utils.js pacificNowAsUtc, which
// this mirrors client-side). "Ago" math must therefore compare against
// Pacific-now-as-UTC, never Date.now().
function pacificNowAsUtcMs() {
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Vancouver', hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date()).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  // en-CA quirk: midnight formats as hour '24' (same guard as the backend helper)
  return Date.UTC(+p.year, +p.month - 1, +p.day, p.hour === '24' ? 0 : +p.hour, +p.minute, +p.second);
}

function agoLabel(iso) {
  if (!iso) return '';
  const s = Math.max(0, Math.floor((pacificNowAsUtcMs() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// Prisma Decimal serializes to a string ("26.00") — normalize, drop .00.
function money(v, { sign = false } = {}) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  const abs = Math.abs(n);
  const body = Number.isInteger(abs) ? String(abs) : abs.toFixed(2);
  const prefix = n < 0 ? '-' : (sign ? '+' : '');
  return `${prefix}$${body}`;
}

// Soft two-tone chime, ~0.25 s, pure WebAudio — no asset, no network.
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const tone = (freq, at, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      g.gain.exponentialRampToValueAtTime(0.16, ctx.currentTime + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + dur);
      o.connect(g);
      g.connect(ctx.destination);
      o.start(ctx.currentTime + at);
      o.stop(ctx.currentTime + at + dur + 0.05);
    };
    tone(880, 0, 0.12);       // A5
    tone(1174.66, 0.11, 0.14); // D6
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    setTimeout(() => { ctx.close().catch(() => {}); }, 700);
  } catch { /* audio blocked — chime is best-effort */ }
}

async function deskFetch(token, path, body) {
  const res = await fetch(`${API_URL}/api/desk/change-requests${path}`, {
    method: body ? 'POST' : 'GET',
    headers: {
      Authorization: `Desk ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON error body */ }
  return { status: res.status, data };
}

const VERB_CLASS = { ADD: 'dn-verb-add', 'ADD-ON': 'dn-verb-addon', SWAP: 'dn-verb-swap', REMOVE: 'dn-verb-remove' };

// ── One open card ───────────────────────────────────────────────────────────
function RequestCard({ row, token, onRowUpdate, onToast, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [verifyBlock, setVerifyBlock] = useState(false); // 409 {verify:'missing'}
  const [notice, setNotice] = useState(null);            // 409 {error, request} text
  const [cantOpen, setCantOpen] = useState(false);
  const [cantReason, setCantReason] = useState('');

  const firstName = row.requestedBy?.firstName || 'the provider';

  // Shared 409/error plumbing for tick + cant.
  const handleFailure = useCallback((status, data) => {
    if (status === 409 && data?.verify === 'missing') {
      setVerifyBlock(true);
      return;
    }
    if (status === 409 && data) {
      // Stamp mismatch / status conflict: adopt the server's fresh row (its
      // guestApprovedAt is the new stamp) and let the desk re-read.
      if (data.request) onRowUpdate(data.request);
      else onRefresh();
      setNotice(data.error || 'This request just changed — take another look.');
      return;
    }
    setNotice(data?.error || 'Server error — try again.');
  }, [onRowUpdate, onRefresh]);

  const doTick = async (override) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const body = { seenApprovedAt: row.guestApprovedAt };
      if (override) body.override = true;
      const { status, data } = await deskFetch(token, `/${row.id}/tick`, body);
      if (status === 200 && data?.request) {
        setVerifyBlock(false);
        onRowUpdate(data.request);
        onToast(`Marked done — ${firstName} notified`);
      } else {
        handleFailure(status, data);
      }
    } catch {
      setNotice("Couldn't reach the server — try again.");
    } finally {
      setBusy(false);
    }
  };

  const doCant = async () => {
    const reason = cantReason.trim();
    if (!reason || busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const { status, data } = await deskFetch(token, `/${row.id}/cant`, {
        reason, seenApprovedAt: row.guestApprovedAt,
      });
      if (status === 200 && data?.request) {
        onRowUpdate(data.request);
        onToast(`Sent back to ${firstName}`);
      } else {
        handleFailure(status, data);
      }
    } catch {
      setNotice("Couldn't reach the server — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`dn-card ${row.kioskWaitingAt ? 'dn-card-kiosk' : ''}`}>
      {row.kioskWaitingAt && <div className="dn-kiosk-tag">GUEST AT KIOSK</div>}
      <div className="dn-card-main">
        <button
          type="button"
          className="dn-tick"
          disabled={busy}
          onClick={() => doTick(false)}
          aria-label={`Mark ${row.guestName}'s change as keyed into Zenoti`}
          title="Keyed into Zenoti"
        >
          <span className="dn-tick-mark">✓</span>
        </button>
        <div className="dn-card-body">
          <div className="dn-guest">{row.guestName}</div>
          {(Array.isArray(row.display) ? row.display : []).map((l, i) => (
            <div className="dn-line" key={i}>
              <span className={`dn-verb ${VERB_CLASS[l.verb] || 'dn-verb-add'}`}>{l.verb}</span>
              <span className="dn-line-text">
                <span className="dn-line-label">{l.label}</span>
                {l.sub && <span className="dn-line-sub">{l.sub}</span>}
                {l.provider && <span className="dn-line-provider">{l.provider}</span>}
              </span>
              {/* ADD/ADD-ON prices are plain amounts; SWAP is a signed delta
                  (see backend/lib/change-requests/guards.js display builder) —
                  mockup shows "$22" for ADD but "+$26" for SWAP. */}
              {l.price != null && <span className="dn-line-price">{money(l.price, { sign: l.verb === 'SWAP' })}</span>}
            </div>
          ))}
          <div className="dn-total-row">
            <span>Invoice change {money(row.priceDelta, { sign: true })}{row.newTotal != null ? ` → ${money(row.newTotal)}` : ''}</span>
            <span className="dn-price-ok">Guest OK'd the price ✓</span>
          </div>
          {row.note && <div className="dn-note">“{row.note}”</div>}
          {row.guardReason && (
            <div className="dn-guard-reason">{row.guardReason}</div>
          )}
          <div className="dn-meta">
            {firstName} · {agoLabel(row.createdAt)}
            {!cantOpen && (
              <button type="button" className="dn-cant-link" onClick={() => setCantOpen(true)}>
                can't do it
              </button>
            )}
          </div>

          {notice && <div className="dn-conflict">{notice}</div>}

          {verifyBlock && (
            <div className="dn-verify">
              <div className="dn-verify-text">Those lines aren't on the invoice yet</div>
              <div className="dn-verify-actions">
                <button type="button" className="dn-btn dn-btn-ghost" disabled={busy} onClick={() => doTick(false)}>
                  Re-check
                </button>
                <button type="button" className="dn-btn dn-btn-amber" disabled={busy} onClick={() => doTick(true)}>
                  Tick anyway
                </button>
              </div>
            </div>
          )}

          {cantOpen && (
            <div className="dn-cant-form">
              <input
                className="dn-cant-input"
                type="text"
                autoFocus
                placeholder={`Why not? — goes back to ${firstName}`}
                value={cantReason}
                maxLength={200}
                onChange={(e) => setCantReason(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') doCant(); }}
              />
              <div className="dn-verify-actions">
                <button type="button" className="dn-btn dn-btn-ghost" disabled={busy} onClick={() => { setCantOpen(false); setCantReason(''); }}>
                  Cancel
                </button>
                <button type="button" className="dn-btn dn-btn-red" disabled={busy || !cantReason.trim()} onClick={doCant}>
                  Send
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Non-actionable cards (shared by the single- and multi-location layouts) ──
function ApplyingCard({ row }) {
  return (
    <div className="dn-card dn-card-applying">
      <div className="dn-guest">{row.guestName}</div>
      <span className="dn-applying-tag">auto-applying…</span>
    </div>
  );
}

function AutoAppliedCard({ row }) {
  return (
    <div className="dn-card dn-card-auto">
      <div className="dn-auto-head">
        <span className="dn-auto-check">✓</span>
        <span className="dn-guest">{row.guestName}</span>
        <span className="dn-auto-tag">AUTO-APPLIED</span>
      </div>
      {(Array.isArray(row.display) ? row.display : []).map((l, i) => (
        <div className="dn-auto-line" key={i}>{l.verb} {l.label}{l.price != null ? ` ${money(l.price, { sign: l.verb === 'SWAP' })}` : ''}</div>
      ))}
      <div className="dn-auto-note">Already in Zenoti — no action needed</div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function DeskNotes() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);
  const [rows, setRows] = useState(null); // null = not loaded yet
  // Token scope from the poll ({ label, allCenters, centerIds }). Multi-center
  // tokens render stacked per-location sections; All-tokens add filter chips.
  const [device, setDevice] = useState(null);
  const [filter, setFilter] = useState('all'); // All-token chip: 'all' | centerId (per tab, not persisted)
  const deviceRef = useRef(null);
  const filterRef = useRef('all');
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTED_KEY) === '1');
  // Persisted: Zenoti full-reloads between pages, and a panel that popped back
  // open on every navigation sat on top of Zenoti's buttons.
  const [minimized, setMinimizedState] = useState(() => localStorage.getItem(MINIMIZED_KEY) === '1');
  const setMinimized = useCallback((next) => {
    setMinimizedState(next);
    localStorage.setItem(MINIMIZED_KEY, next ? '1' : '0');
  }, []);
  const pillRef = useRef(null);
  const [panelSize, setPanelSize] = useState(() => readJson(SIZE_KEY, DEFAULT_SIZE, 'width', 'height'));
  const [pos, setPos] = useState(() => readJson(POS_KEY, DEFAULT_POS, 'left', 'bottom'));
  const [pillSize, setPillSize] = useState(null);
  const [viewport, setViewport] = useState(null); // from content.js; null until it answers
  const suppressPillClickRef = useRef(false);

  // The iframe's on-screen box: size for the current state, anchor clamped so
  // the panel can never sit (or be dragged) off-screen, including after the
  // Zenoti window shrinks. null until content.js reports the viewport.
  const placement = useMemo(() => {
    if (!viewport) return null;
    const vw = viewport.width;
    const vh = viewport.height;
    const pill = !!(token && minimized && pillSize);
    const w = pill ? pillSize.width : Math.min(token ? panelSize.width : DEFAULT_SIZE.width, vw - 8);
    const h = pill ? pillSize.height : Math.min(token ? panelSize.height : DEFAULT_SIZE.height, vh - 8);
    return {
      vw, vh, w, h, pill,
      left: clamp(pos.left, 4, vw - w - 4),
      bottom: clamp(pos.bottom, 4, vh - h - 4),
    };
  }, [viewport, token, minimized, pillSize, panelSize, pos]);

  // Shared pointer-drag plumbing. screenX/Y, not clientX/Y: the iframe itself
  // moves/resizes under the pointer mid-drag, so frame-relative coords jitter.
  const beginDrag = useCallback((e, onDelta, onDone) => {
    if (e.button !== 0) return;
    const el = e.currentTarget;
    try { el.setPointerCapture(e.pointerId); } catch { /* pointer already gone */ }
    const sx = e.screenX;
    const sy = e.screenY;
    let moved = false;
    const onMove = (ev) => {
      const dx = ev.screenX - sx;
      const dy = ev.screenY - sy;
      if (!moved && Math.abs(dx) + Math.abs(dy) < 4) return; // a click, not a drag
      moved = true;
      onDelta(dx, dy);
    };
    const onUp = () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      onDone(moved);
    };
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
  }, []);

  // Resize from any edge/corner. dir ⊂ 'nsew'. The panel is anchored by its
  // bottom-left corner, so dragging the west or south edge moves that anchor
  // as well as the size; north/east only change the size.
  const startResize = useCallback((dir) => (e) => {
    e.preventDefault();
    e.stopPropagation(); // the top edge sits over the drag-to-move header
    const s0 = {
      ...panelSize,
      left: placement ? placement.left : pos.left,
      bottom: placement ? placement.bottom : pos.bottom,
    };
    const maxH = placement ? placement.vh - 8 : Infinity;
    let latest = s0;
    beginDrag(e, (dx, dy) => {
      let { width, height, left, bottom } = s0;
      if (dir.includes('e')) width = clamp(s0.width + dx, MIN_SIZE.width, MAX_WIDTH);
      if (dir.includes('w')) {
        width = clamp(s0.width - dx, MIN_SIZE.width, MAX_WIDTH);
        left = s0.left + (s0.width - width);
        if (left < 4) { width -= 4 - left; left = 4; }
      }
      if (dir.includes('n')) height = clamp(s0.height - dy, MIN_SIZE.height, maxH);
      if (dir.includes('s')) {
        height = clamp(s0.height + dy, MIN_SIZE.height, maxH);
        bottom = s0.bottom - (height - s0.height);
        if (bottom < 4) { height -= 4 - bottom; bottom = 4; }
      }
      latest = { width: Math.round(width), height: Math.round(height), left: Math.round(left), bottom: Math.round(bottom) };
      setPanelSize({ width: latest.width, height: latest.height });
      setPos({ left: latest.left, bottom: latest.bottom });
    }, (moved) => {
      if (!moved) return;
      localStorage.setItem(SIZE_KEY, JSON.stringify({ width: latest.width, height: latest.height }));
      localStorage.setItem(POS_KEY, JSON.stringify({ left: latest.left, bottom: latest.bottom }));
    });
  }, [panelSize, pos, placement, beginDrag]);

  const startMove = useCallback((e) => {
    if (e.target.closest('button:not(.dn-pill)')) return; // header buttons stay clickable
    const start = placement ? { left: placement.left, bottom: placement.bottom } : pos;
    let latest = start;
    beginDrag(e, (dx, dy) => {
      // clamp as we go so the saved spot is always the on-screen one
      latest = placement ? {
        left: Math.round(clamp(start.left + dx, 4, placement.vw - placement.w - 4)),
        bottom: Math.round(clamp(start.bottom - dy, 4, placement.vh - placement.h - 4)),
      } : { left: Math.round(start.left + dx), bottom: Math.round(start.bottom - dy) };
      setPos(latest);
    }, (moved) => {
      if (!moved) return;
      suppressPillClickRef.current = true; // the pointerup's click must not expand the pill
      setTimeout(() => { suppressPillClickRef.current = false; }, 0);
      localStorage.setItem(POS_KEY, JSON.stringify(latest));
    });
  }, [pos, placement, beginDrag]);
  const [pollFailed, setPollFailed] = useState(false);
  const [showDone, setShowDone] = useState(false); // footer toggle: today's handled requests
  const [toast, setToast] = useState(null);
  const [setupInput, setSetupInput] = useState('');
  const [setupError, setSetupError] = useState(null);
  const [setupBusy, setSetupBusy] = useState(false);

  const prevOpenIdsRef = useRef(null); // null sentinel: no chime on first load
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const toastTimerRef = useRef(null);

  // Transparent page background so the minimized pill doesn't sit in a white
  // 348x520 box on top of Zenoti (the iframe shell is fixed-size).
  useEffect(() => {
    document.documentElement.classList.add('desk-notes-page');
    document.body.classList.add('desk-notes-page');
    return () => {
      document.documentElement.classList.remove('desk-notes-page');
      document.body.classList.remove('desk-notes-page');
    };
  }, []);

  // Viewport handshake: ask on mount, listen for answers (content.js also
  // re-sends on window resize).
  useEffect(() => {
    if (window.parent === window) return undefined; // opened directly, not via the extension
    const onMsg = (e) => {
      if (e.source !== window.parent || !EXT_ORIGIN_OK(e.origin)) return;
      const d = e.data || {};
      if (d.type === 'cp-desk-notes:viewport' && Number.isFinite(d.width) && Number.isFinite(d.height)) {
        setViewport({ width: d.width, height: d.height });
      }
    };
    window.addEventListener('message', onMsg);
    window.parent.postMessage({ type: 'cp-desk-notes:hello' }, ZENOTI_ORIGIN);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // Pill size (its text changes with the open count).
  useLayoutEffect(() => {
    const el = pillRef.current;
    if (!token || !minimized || !el) { setPillSize(null); return undefined; }
    const measure = () => {
      const r = el.getBoundingClientRect();
      setPillSize({ width: Math.ceil(r.width), height: Math.ceil(r.height) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [token, minimized]);

  // Where the iframe goes. Everything is clamped so the panel can never be
  // dragged or resized off-screen (or left off-screen by a smaller window).
  useLayoutEffect(() => {
    if (window.parent === window || !placement) return;
    const { vh, w, h, left, bottom, pill } = placement;
    const inset = pill ? PILL_INSET : 0;
    window.parent.postMessage({
      type: 'cp-desk-notes:rect',
      left: left - inset,
      top: vh - bottom - h - inset,
      width: w + inset * 2,
      height: h + inset * 2,
    }, ZENOTI_ORIGIN);
  }, [placement]);

  const showToast = useCallback((text) => {
    setToast(text);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 3500);
  }, []);
  useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);

  const ingest = useCallback((requests, dev) => {
    setRows(requests);
    if (dev) { deviceRef.current = dev; setDevice(dev); }
    const open = requests.filter((r) => r.status === 'open');
    const openIds = open.map((r) => r.id);
    // "kiosk:<id>" marks a guest newly waiting at the kiosk on an open card —
    // as urgent as a brand-new request.
    const waitingKeys = open.filter((r) => r.kioskWaitingAt).map((r) => `kiosk:${r.id}`);
    const seen = [...openIds, ...waitingKeys];
    const prev = prevOpenIdsRef.current;
    const fresh = prev === null ? [] : seen.filter((k) => !prev.includes(k));
    if (fresh.length > 0) {
      // Several Zenoti tabs on one desk each run this panel. The first tab to
      // notice a new key claims it in shared localStorage and chimes; the rest
      // stay quiet (see lib/chimeClaim.js). Pop-open below is per tab.
      const now = Date.now();
      const won = claimChime(localStorage, fresh, now);
      if (won.length > 0 && !mutedRef.current) playChime();
      sweepChimeClaims(localStorage, now);
      // All-token filter chip: never let a new request hide behind it.
      if (deviceRef.current?.allCenters) {
        const freshIds = new Set(fresh.map((k) => k.replace(/^kiosk:/, '')));
        const freshRow = requests.find((r) => freshIds.has(r.id));
        if (freshRow) {
          const nf = nextFilter(filterRef.current, freshRow.centerId);
          if (nf !== filterRef.current) { filterRef.current = nf; setFilter(nf); }
        }
      }
      // Pop the panel open — a pink pill in a corner is easy to miss. Only on
      // something NEW, so a desk that re-minimizes stays minimized until the
      // next request (mute silences the chime, never the pop-open).
      setMinimized(false);
    }
    prevOpenIdsRef.current = seen;
  }, [setMinimized]);

  const poll = useCallback(async (tok) => {
    try {
      const { status, data } = await deskFetch(tok, '');
      if (status === 401) {
        // Revoked / bad token → back to setup.
        localStorage.removeItem(TOKEN_KEY);
        prevOpenIdsRef.current = null;
        setToken(null);
        setRows(null);
        setSetupError("This desk's token stopped working — check with Lindsay.");
        return;
      }
      if (status === 200 && Array.isArray(data?.requests)) {
        ingest(data.requests, data.device || null);
        setPollFailed(false);
      } else {
        setPollFailed(true);
      }
    } catch {
      setPollFailed(true); // keep last-known rows on network blips
    }
  }, [ingest]);

  // Poll every 15 s while we have a token; immediately on mount/token-save.
  useEffect(() => {
    if (!token) return undefined;
    poll(token);
    const id = setInterval(() => poll(token), POLL_MS);
    return () => clearInterval(id);
  }, [token, poll]);

  const saveToken = async () => {
    const candidate = setupInput.trim();
    if (!candidate || setupBusy) return;
    setSetupBusy(true);
    setSetupError(null);
    try {
      const { status, data } = await deskFetch(candidate, '');
      if (status === 200 && Array.isArray(data?.requests)) {
        localStorage.setItem(TOKEN_KEY, candidate);
        prevOpenIdsRef.current = null;
        setSetupInput('');
        setToken(candidate); // poll effect fires immediately
        ingest(data.requests, data.device || null);
      } else if (status === 401) {
        setSetupError("That token didn't work — check with Lindsay.");
      } else {
        setSetupError("Couldn't reach the server — try again.");
      }
    } catch {
      setSetupError("Couldn't reach the server — try again.");
    } finally {
      setSetupBusy(false);
    }
  };

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      localStorage.setItem(MUTED_KEY, next ? '1' : '0');
      return next;
    });
  };

  // tick/cant responses (and 409 replacement rows) come back WITHOUT the
  // requestedBy include — keep the one we already have for the card meta.
  const onRowUpdate = useCallback((updated) => {
    setRows((rs) => (rs || []).map((r) => (r.id === updated.id ? { ...r, ...updated, requestedBy: updated.requestedBy || r.requestedBy } : r)));
  }, []);
  const onRefresh = useCallback(() => { if (token) poll(token); }, [token, poll]);

  // ── Setup state (no token) ──
  if (!token) {
    return (
      <div className="dn-root">
        <div className="dn-panel">
          <div className="dn-header">
            <span className="dn-header-title">Change requests</span>
          </div>
          <div className="dn-setup">
            <p className="dn-setup-text">
              This panel shows service changes the wax room sends to the desk.
              Paste this computer's desk token to connect it (Lindsay mints
              these in ops).
            </p>
            <input
              className="dn-setup-input"
              type="text"
              placeholder="Paste desk token"
              value={setupInput}
              onChange={(e) => setSetupInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveToken(); }}
            />
            <button type="button" className="dn-btn dn-btn-brand" disabled={setupBusy || !setupInput.trim()} onClick={saveToken}>
              {setupBusy ? 'Checking…' : 'Save'}
            </button>
            {setupError && <div className="dn-setup-error">{setupError}</div>}
          </div>
        </div>
      </div>
    );
  }

  const all = rows || [];
  const open = all.filter((r) => r.status === 'open');
  const openKiosk = open.filter((r) => r.kioskWaitingAt);
  const openRest = open.filter((r) => !r.kioskWaitingAt); // server order = newest first
  const applying = all.filter((r) => r.status === 'applying');
  const autoApplied = all.filter((r) => r.status === 'auto_applied');
  const doneToday = all.filter((r) => r.status === 'done' || r.status === 'cant' || r.status === 'auto_applied').length;
  // Desk-handled history (auto-applied rows already show above). Newest first.
  const handled = all
    .filter((r) => r.status === 'done' || r.status === 'cant')
    .sort((a, b) => new Date(b.resolvedAt || b.createdAt) - new Date(a.resolvedAt || a.createdAt));

  // Multi-location layout (some / all tokens). Single-center tokens skip this.
  const multi = isMulti(device);
  const sections = multi ? buildSections(all, device, LOCATIONS) : [];
  const showChips = multi && device?.allCenters === true;
  const visibleSections = showChips && filter !== 'all' ? sections.filter((s) => s.centerId === filter) : sections;
  const chooseFilter = (f) => { filterRef.current = f; setFilter(f); };

  // ── Minimized pill ──
  if (minimized) {
    return (
      <div className="dn-root">
        <button
          ref={pillRef}
          type="button"
          className={`dn-pill ${open.length > 0 ? 'dn-pill-active' : ''}`}
          onPointerDown={startMove}
          onClick={() => { if (!suppressPillClickRef.current) setMinimized(false); }}
          title="Click to open · drag to move"
        >
          {open.length > 0 ? (
            <><span className="dn-pulse" /> {open.length} change request{open.length === 1 ? '' : 's'}</>
          ) : (
            'No open requests'
          )}
        </button>
      </div>
    );
  }

  // ── Expanded panel ──
  return (
    <div className="dn-root">
      <div className="dn-panel">
        {['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'].map((d) => (
          <div key={d} className={`dn-resize dn-resize-${d}`} onPointerDown={startResize(d)} aria-hidden="true" />
        ))}
        <div className="dn-header dn-header-drag" onPointerDown={startMove} title="Drag to move">
          {open.length > 0 && <span className="dn-pulse" />}
          <span className="dn-header-title">Change requests</span>
          <span className="dn-count-pill">{open.length}</span>
          <button
            type="button"
            className={`dn-mute ${muted ? 'dn-mute-on' : ''}`}
            onClick={toggleMute}
            title={muted ? 'Chime off — click to unmute' : 'Chime on — click to mute'}
            aria-label={muted ? 'Unmute new-request chime' : 'Mute new-request chime'}
          >
            {muted ? '🔕' : '🔔'}
          </button>
          <button type="button" className="dn-minimize" onClick={() => setMinimized(true)} aria-label="Minimize panel">
            —
          </button>
        </div>

        {pollFailed && <div className="dn-offline">Reconnecting…</div>}

        <div className="dn-body">
          {rows === null && <div className="dn-empty">Loading…</div>}

          {rows !== null && !multi && open.length === 0 && applying.length === 0 && autoApplied.length === 0 && (
            <div className="dn-empty">All clear ✨</div>
          )}

          {!multi && openKiosk.map((r) => (
            <RequestCard key={r.id} row={r} token={token} onRowUpdate={onRowUpdate} onToast={showToast} onRefresh={onRefresh} />
          ))}
          {!multi && openRest.map((r) => (
            <RequestCard key={r.id} row={r} token={token} onRowUpdate={onRowUpdate} onToast={showToast} onRefresh={onRefresh} />
          ))}
          {!multi && applying.map((r) => <ApplyingCard key={r.id} row={r} />)}
          {!multi && autoApplied.map((r) => <AutoAppliedCard key={r.id} row={r} />)}

          {multi && showChips && (
            <div className="dn-loc-chips" role="tablist" aria-label="Filter by location">
              <button type="button" className={`dn-loc-chip ${filter === 'all' ? 'dn-loc-chip-on' : ''}`} onClick={() => chooseFilter('all')}>
                All · {open.length}
              </button>
              {sections.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className={`dn-loc-chip ${filter === s.centerId ? 'dn-loc-chip-on' : ''}`}
                  style={{ '--loc': s.color, '--loc-bg': s.bg }}
                  onClick={() => chooseFilter(s.centerId)}
                  disabled={!s.centerId}
                >
                  {s.short} · {s.openCount}
                </button>
              ))}
            </div>
          )}

          {multi && rows !== null && visibleSections.map((s) => (
            <section className="dn-loc-section" key={s.key} style={{ '--loc': s.color, '--loc-bg': s.bg }}>
              <div className="dn-loc-band">
                <span className="dn-loc-band-label">{s.label}</span>
                <span className="dn-loc-band-count">{s.openCount}</span>
              </div>
              {s.openCount === 0 && s.applying.length === 0 && s.autoApplied.length === 0 && (
                <div className="dn-loc-empty">—</div>
              )}
              {s.kiosk.map((r) => (
                <RequestCard key={r.id} row={r} token={token} onRowUpdate={onRowUpdate} onToast={showToast} onRefresh={onRefresh} />
              ))}
              {s.rest.map((r) => (
                <RequestCard key={r.id} row={r} token={token} onRowUpdate={onRowUpdate} onToast={showToast} onRefresh={onRefresh} />
              ))}
              {s.applying.map((r) => <ApplyingCard key={r.id} row={r} />)}
              {s.autoApplied.map((r) => <AutoAppliedCard key={r.id} row={r} />)}
            </section>
          ))}

          {showDone && (
            <div className="dn-history">
              <div className="dn-history-title">Handled today</div>
              {handled.length === 0 && <div className="dn-empty">Nothing handled yet today.</div>}
              {handled.map((r) => (
                <div className={`dn-card dn-card-history ${r.status === 'cant' ? 'dn-card-history-cant' : ''}`} key={r.id}>
                  <div className="dn-auto-head">
                    <span className="dn-guest">{r.guestName}</span>
                    <span className={r.status === 'cant' ? 'dn-history-tag-cant' : 'dn-history-tag-done'}>
                      {r.status === 'cant' ? "✕ COULDN'T" : '✓ DONE'}
                    </span>
                  </div>
                  {(Array.isArray(r.display) ? r.display : []).map((l, i) => (
                    <div className="dn-auto-line" key={i}>{l.verb} {l.label}{l.price != null ? ` ${money(l.price, { sign: l.verb === 'SWAP' })}` : ''}</div>
                  ))}
                  {r.status === 'cant' && r.cantReason && <div className="dn-history-reason">“{r.cantReason}”</div>}
                  <div className="dn-history-meta">
                    {r.requestedBy?.firstName || 'Provider'} · {money(r.priceDelta, { sign: true })}{r.newTotal != null ? ` → ${money(r.newTotal)}` : ''} · {agoLabel(r.resolvedAt || r.createdAt)}
                    {r.device?.label ? ` · by ${r.device.label}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="dn-footer">
          <button type="button" className="dn-done-toggle" onClick={() => setShowDone((v) => !v)} aria-expanded={showDone}>
            Done today · {doneToday} {showDone ? '▴' : '▾'}
          </button>
          {/* The ops app is state-routed, not path-routed (see src/index.js's
              /desk-notes seam) — "/change-requests-log" as a bare path would
              just load the default Tasks view. #change-requests-log is the
              one hash App.js recognizes to land on this page (mirrors the
              existing #pkgsales convention). */}
          <a href="/#change-requests-log" target="_blank" rel="noreferrer">Full log in ops →</a>
        </div>

        {toast && <div className="dn-toast">{toast}</div>}
      </div>
    </div>
  );
}
